import { useState, type FormEvent } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Field } from "../components/Field";
import { Footer } from "../components/Footer";
import type { LegalDocumentBundle } from "../legal/types";
import { useLegalDocuments } from "../legal/useLegalDocuments";
import { ApiError, apiSend } from "../lib/customFetch";
import styles from "./AuthLayout.module.css";

interface SignupResponse {
  redirectTo: string;
}

interface BetaInterestResponse {
  besked: string;
  mailStatus: "sendt" | "fejlet" | "deaktiveret";
}

/**
 * Selvbetjent signup — i LUKKET BETA (trin 9, fase 3c): kræver invite-kode.
 * Uden kode kan man skrive sig op (beta_interest); ingen mail endnu.
 * Koden og interesse-fladen fjernes, når B-007 (juradokumenterne) er klar.
 */
export function SignupPage() {
  const [visInteresse, setVisInteresse] = useState(false);
  const legal = useLegalDocuments();

  return (
    <div className={styles.page}>
      <div className={styles.box}>
        <div className={styles.brand}>
          app<span>smeden</span>
        </div>
        {visInteresse ? (
          <InteresseForm onTilbage={() => setVisInteresse(false)} />
        ) : (
          <SignupForm
            legalBundle={legal.bundle}
            legalError={legal.error}
            onRetryLegal={legal.retry}
            onIngenKode={() => setVisInteresse(true)}
          />
        )}
        <p className={styles.footer}>
          Har du allerede en konto? <Link href="/login">Log ind</Link>
        </p>
        <Footer />
      </div>
    </div>
  );
}

function SignupForm({
  legalBundle,
  legalError,
  onRetryLegal,
  onIngenKode,
}: {
  legalBundle: LegalDocumentBundle | null;
  legalError: boolean;
  onRetryLegal: () => void;
  onIngenKode: () => void;
}) {
  const [, navigate] = useLocation();
  const [firmanavn, setFirmanavn] = useState("");
  const [navn, setNavn] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [betaKode, setBetaKode] = useState("");
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!accept) {
      setError("Du skal acceptere vilkårene for at fortsætte.");
      return;
    }
    if (legalBundle === null) {
      setError("Dokumentversionen kunne ikke hentes. Genindlæs siden og prøv igen.");
      return;
    }
    if (password.length < 10) {
      setError("Adgangskoden skal være mindst 10 tegn.");
      return;
    }
    setBusy(true);
    apiSend<SignupResponse>("POST", "/api/signup", {
      firmanavn,
      navn,
      email,
      password,
      accept,
      dokumentVersion: legalBundle.version,
      betaKode: betaKode.trim(),
    })
      .then((res) => navigate(res.redirectTo))
      .catch((err: unknown) => {
        setBusy(false);
        if (err instanceof ApiError && err.status === 403) {
          setError(
            "Invitationskoden er ikke gyldig. Har du ingen kode, kan du skrive dig op nedenfor.",
          );
        } else if (err instanceof ApiError && err.status === 409) {
          setError("Emailadressen er allerede i brug — vil du logge ind i stedet?");
        } else if (err instanceof ApiError && err.status === 429) {
          setError("For mange forsøg — vent lidt og prøv igen.");
        } else {
          setError("Noget gik galt. Kontrollér felterne og prøv igen.");
        }
      });
  };

  return (
    <Card title="Opret din virksomhed">
      <form className={styles.form} onSubmit={submit}>
        <p className={styles.betaNote}>
          AppSmeden er i <strong>lukket beta</strong> — oprettelse kræver en
          invitationskode.
        </p>
        <Field
          label="Invitationskode"
          value={betaKode}
          onChange={(e) => setBetaKode(e.target.value)}
          required
        />
        <Field
          label="Firmanavn"
          placeholder="Fx Hansens VVS ApS"
          value={firmanavn}
          onChange={(e) => setFirmanavn(e.target.value)}
          required
        />
        <Field
          label="Dit navn"
          value={navn}
          onChange={(e) => setNavn(e.target.value)}
          required
        />
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Adgangskode"
          type="password"
          autoComplete="new-password"
          hint="Mindst 10 tegn."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <label className={styles.accept}>
          <input
            type="checkbox"
            checked={accept}
            onChange={(e) => setAccept(e.target.checked)}
            disabled={legalBundle === null}
            required
          />
          <span>
            Jeg accepterer{" "}
            <Link href="/jura/databehandleraftale">databehandleraftalen</Link>
            {" "}og{" "}
            <Link href="/jura/privatlivspolitik">privatlivspolitikken</Link>
            {legalBundle !== null && (
              <> (version {legalBundle.version})</>
            )}.
          </span>
        </label>
        {legalBundle !== null && legalBundle.status === "draft" && (
          <div className={styles.legalDraft} role="status">
            <strong>Vilkår under juridisk godkendelse</strong>
            <span>
              Dokumenterne er kladder og er endnu ikke juridisk godkendt.
            </span>
          </div>
        )}
        {legalBundle === null && !legalError && (
          <p className={styles.betaNote}>Henter dokumentversion…</p>
        )}
        {legalError && (
          <div className={styles.error} role="alert">
            <p>De juridiske dokumenter kunne ikke hentes. Oprettelse er sat på pause.</p>
            <Button variant="secondary" onClick={onRetryLegal}>
              Prøv igen
            </Button>
          </div>
        )}
        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <Button type="submit" disabled={busy || legalBundle === null}>
          {busy ? "Opretter…" : "Opret virksomhed"}
        </Button>
        <button
          type="button"
          className={styles.linkKnap}
          onClick={onIngenKode}
        >
          Ingen invitationskode? Skriv dig op, så giver vi lyd.
        </button>
      </form>
    </Card>
  );
}

function InteresseForm({ onTilbage }: { onTilbage: () => void }) {
  const [email, setEmail] = useState("");
  const [navn, setNavn] = useState("");
  const [firma, setFirma] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resultat, setResultat] = useState<BetaInterestResponse | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    apiSend<BetaInterestResponse>("POST", "/api/beta-interest", {
      email,
      ...(navn.trim().length > 0 ? { navn: navn.trim() } : {}),
      ...(firma.trim().length > 0 ? { firma: firma.trim() } : {}),
    })
      .then(setResultat)
      .catch((err: unknown) => {
        setBusy(false);
        setError(
          err instanceof ApiError && err.status === 429
            ? "For mange forsøg — vent lidt og prøv igen."
            : "Noget gik galt — tjek emailadressen og prøv igen.",
        );
      });
  };

  if (resultat !== null) {
    return (
      <Card title="Tak — du står på listen">
        <p className={styles.betaNote}>
          Vi giver lyd på {email}, når døren åbner. Ingen nyhedsbreve,
          ingen støj — bare én besked, når du kan komme i gang.
        </p>
        {resultat.mailStatus === "sendt" && (
          <p className={styles.mailStatus}>Vi har også sendt en kvittering.</p>
        )}
        {resultat.mailStatus === "fejlet" && (
          <p className={styles.legalDraft} role="status">
            Du står på listen, men kvitteringsmailen kunne ikke sendes.
          </p>
        )}
        {resultat.mailStatus === "deaktiveret" && (
          <p className={styles.legalDraft} role="status">
            Du står på listen. Mailkvitteringer er ikke aktiveret endnu.
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card title="Lukket beta — skriv dig op">
      <form className={styles.form} onSubmit={submit}>
        <p className={styles.betaNote}>
          AppSmeden åbner snart for flere virksomheder. Læg din email, så
          giver vi lyd, når døren åbner.
        </p>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Dit navn (valgfrit)"
          value={navn}
          onChange={(e) => setNavn(e.target.value)}
        />
        <Field
          label="Firma (valgfrit)"
          value={firma}
          onChange={(e) => setFirma(e.target.value)}
        />
        {error !== null && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? "Sender…" : "Skriv mig op"}
        </Button>
        <button type="button" className={styles.linkKnap} onClick={onTilbage}>
          Jeg har en invitationskode
        </button>
      </form>
    </Card>
  );
}
